import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useInputAddress } from './useInputAddress';
import Geosuggest from 'react-geosuggest';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '~/UIKit/Style';
import { HookClassesOverride } from '~/Helpers/TypeScript.helpers';
import { FONT_SIZE_MEDIUM, FONT_SIZE_LARGISH } from '~/Constants/UI.constants';
import {
  CHARCOAL_GREY,
  LIGHT_BLUE,
  PLAIN_GREY,
  VERTICAL_SEPARATOR,
  WHITE,
} from '~/Constants/Colors.constants';

const useStyles = (simple: boolean) =>
  makeStyles(() => ({
    inputAddress: {
      caretColor: CHARCOAL_GREY,
      padding: 0,
      fontSize: 'inherit',
      borderBottom: 'none',
      textAlign: simple ? 'left' : 'right',
      '&:hover, &:focus': {
        borderBottom: 'none',
      },
      '&::placeholder': {
        color: simple ? 'currentColor' : PLAIN_GREY,
      },
    },
    inputAddressContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      minHeight: 50,
      padding: simple ? 0 : '0 15px',
      borderBottom: `thin solid ${simple ? 'currentColor' : VERTICAL_SEPARATOR}`,
      backgroundColor: WHITE,
      position: 'relative',
      opacity: 1,
      fontSize: FONT_SIZE_MEDIUM,
    },
    inputAddressIcon: {
      backgroundColor: LIGHT_BLUE,
      borderRadius: 30,
      minWidth: 30,
      maxWidth: 30,
      minHeight: 30,
      maxHeight: 30,
      lineHeight: '15px',
      fontSize: FONT_SIZE_LARGISH,
      display: 'flex',
      alignItems: 'center',
      placeContent: 'center',
      marginRight: 15,
      color: CHARCOAL_GREY,
      '& > svg': {
        fontSize: 'inherit',
      },
    },
    suggestsWrapper: {
      flexGrow: 1,
      '&>.geosuggest__suggests-wrapper': {
        '&>ul': {
          '&:before': {
            right: 10,
            left: 'auto',
          },
          '&:after': {
            right: 11,
            left: 'auto',
          },
        },
      },
    },
    inputAddressLabel: {
      position: 'relative',
      fontSize: 'inherit',
      color: CHARCOAL_GREY,
      marginRight: 15,
    },
  }));

export interface IInputAddressProps {
  initialValue?: string;
  placeholder?: string;
  label: string;
  inputClasses?: HookClassesOverride<typeof useStyles>;
  icon?: JSX.Element;
  onLocationSelect: (address: IAddress) => void;
  onChange?: (value: string) => void;
  disabled?: boolean;
  simple?: boolean;
  countries?: string[];
  hideIcon?: boolean;
}

export interface IAddress {
  city?: string;
  countryISOA2?: string;
  latitude?: number;
  longitude?: number;
  street?: string;
  zipCode?: string;
  countryName?: string;
}

export const InputAddress = ({
  initialValue,
  placeholder,
  label,
  inputClasses = {},
  icon,
  onChange,
  onLocationSelect,
  disabled = false,
  simple = false,
  hideIcon = false,
}: IInputAddressProps) => {
  const {
    onSuggestSelect,
    city,
    zipCode,
    street,
    countryISOA2,
    latitude,
    longitude,
    countryName,
  } = useInputAddress();
  const { t } = useTranslation();
  const { classes } = useStyles(simple)();
  const [valueFromState, setValueInState] = useState<string>();
  const [hasFocus, setHasFocus] = useState(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const checkReady = () => {
      setTimeout(() => {
        if (!isReady && window?.google) {
          setIsReady(true);
        } else if (!isReady) {
          checkReady();
        }
      }, 500);
    };

    checkReady();
    return checkReady;
  }, [window?.google]);

  useEffect(() => {
    if (!street) {
      return;
    }
    onLocationSelect({
      city,
      zipCode,
      street,
      countryISOA2,
      latitude,
      longitude,
      countryName,
    });
  }, [city, zipCode, street, countryISOA2, latitude, longitude, countryName]);

  useEffect(() => {
    if (initialValue) {
      setValueInState(initialValue);
    }
  }, [initialValue, hasFocus, street]);

  const handleFocus = () => {
    setHasFocus(true);
    const inputAddressElement = document.getElementById('geosuggest__input') as HTMLInputElement;
    if (inputAddressElement) {
      setTimeout(() => {
        // we need a timeout in order to change reset input value without updating the state
        inputAddressElement.value = '';
      }, 0);
    }
  };

  const handleBlur = () => {
    setHasFocus(false);
    setValueInState(initialValue);
  };
  return (
    <div className={classes.inputAddressContainer}>
      {icon && !simple && !hideIcon && <div className={classes.inputAddressIcon}>{icon}</div>}
      {!simple && (
        <label htmlFor="geosuggest__input" className={classes.inputAddressLabel}>
          {label}
        </label>
      )}
      {isReady && (
        <Geosuggest
          name="googleSuggest"
          onFocus={simple ? undefined : handleFocus}
          onBlur={() => handleBlur()}
          onSuggestSelect={onSuggestSelect}
          onChange={onChange}
          className={classes.suggestsWrapper}
          inputClassName={classNames(classes.inputAddress, inputClasses)}
          initialValue={valueFromState}
          placeholder={placeholder || t('form.field.location.address')}
          types={['address']}
          disabled={disabled}
          autoComplete="off"
          data-lpignore="true"
        />
      )}
    </div>
  );
};
